import BaseOption from "%/dynamic-component-editor/classes/base-option";
import { useStyleStore } from "@vcl";
import OptionExpression from "@bloglovin/inspire-engine/Helpers/OptionExpression.js";
import { inspectorTools } from "%/dynamic-component-editor/inspector/inspector-tool-map";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";

const MSG_NOT_A_SIDE_MAP = "border-sides must be a {top, right, bottom, left} map or a clock ring; a bare side name or list is not a border";

// eslint-disable-next-line global-require
const useDynamicComponentStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-store").default(...args);


export const BORDER_SIDES_EMPTY_DEFAULT_VALUE = {
  color: {
    value: "none",
    source: "theme.border",
  },
  width: "none",
  style: "solid",
};
export default class BorderSides extends BaseOption {
  static userInterfaceComponent = {
    label: "Borders",
    component: inspectorTools.NEW_BORDER_PICKER,
    useCustomLabel: true,
    useVerticalOrientation: true,
    useDefaultVariableLinking: false,
    props: {
      heading: "Borders",
    },
  };

  static defaultValue = {
    top: null,
    right: null,
    bottom: null,
    left: null,
  };

  static convertConfigValueToCompiledValue(configValue, themeId, themeMode = "inherit") {
    if (configValue && configValue.mode === "clock") {
      return {
        mode: "clock",
        percent: this.clampPercent(configValue.percent),
        thickness: this.normaliseThickness(configValue.thickness),
        completeColor: this.resolveClockColor(configValue.completeColor, themeId, themeMode),
        incompleteColor: this.resolveClockColor(configValue.incompleteColor, themeId, themeMode),
      };
    }

    if (configValue !== null && (typeof configValue !== "object" || Array.isArray(configValue))) {
      throw new DynamicComponentError(MSG_NOT_A_SIDE_MAP, { storedValue: configValue });
    }

    let cssObject = {};

    const option = JSON.parse(JSON.stringify(configValue));

    Object.keys(option).forEach(placement => {
      cssObject[placement] = {};
      if (option[placement] === null) {
        option[placement] = JSON.parse(JSON.stringify(BORDER_SIDES_EMPTY_DEFAULT_VALUE));
      }

      Object.keys(option[placement]).forEach(attribute => {
        let val = option[placement][attribute];
        switch (attribute) {
          case "width":
            cssObject[placement][attribute] = this.getWidth(val, themeId);
            break;
          case "color":
            cssObject[placement][attribute] = this.getColor(val, themeId, themeMode);
            break;
          default:
            cssObject[placement][attribute] = val;
            break;
        }
      });
    });

    return cssObject;
  }

  static clampPercent(percent) {
    if (OptionExpression.isToken(percent)) {
      return percent;
    }
    const n = Number(percent);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
  }

  static normaliseThickness(thickness) {
    if (thickness === null || thickness === undefined || thickness === "") {
      return "0px";
    }
    if (typeof thickness === "number") {
      return `${thickness}px`;
    }
    const trimmed = String(thickness).trim();
    return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
  }

  static resolveClockColor(colorValue, themeId, themeMode = "inherit") {
    if (!colorValue || typeof colorValue !== "object" || !colorValue.source) {
      return typeof colorValue === "string" ? colorValue : "transparent";
    }
    return this.getColor(colorValue, themeId, themeMode);
  }

  static getColor(option, themeId, themeMode = "inherit") {
    const styleStore = useStyleStore();

    if (!themeId) {
      themeId = useDynamicComponentStore().themeId;
    }

    let palette = styleStore.themePalettes[themeId];

    if (option.source.includes("semantic")) {
      palette = styleStore.getSemanticPaletteFromMode(themeId, themeMode);
    }
    return BaseOption.getColorValue(option, styleStore.themes[themeId], palette, themeId, themeMode);
  }

  static getWidth(option, themeId) {
    if (!themeId) {
      themeId = useDynamicComponentStore().themeId;
    }

    let width = BaseOption._getStyleValuePropertiesFromTheme("size.border", themeId)[option];
    if (!width) {
      width = option;
    }
    return width;
  }

  getWidth(option, themeId) {
    return this.constructor.getWidth(option, themeId);
  }

  getColor(option, themeId, themeMode) {
    return this.constructor.getColor(option, themeId, themeMode);
  }
}
